import React, { useEffect, useState } from "react";
import { Download, Smartphone, X } from "lucide-react";
import { isIOS, usePWAInstall } from "./PWAInstallContext";
import InstallInstructions from "./InstallInstructions";
import { supabase } from "../../supabaseClient";

// A dismissal is scoped to a single Supabase login, not to a browser tab.
// This means the banner returns after a fresh sign-in, even if the user
// previously tapped Maybe later. Refreshing/navigating during the SAME login
// does not repeatedly show it.
const DISMISS_KEY = "gwin-install-banner-dismissed-login-v2";

function getLoginKey(session) {
  const userId = session?.user?.id;
  if (!userId) return null;

  // Supabase issues a new JWT session_id for each new sign-in. It stays the
  // same across token refreshes, making it safer than access_token/expiry.
  let sessionId = "";
  try {
    const part = session?.access_token?.split(".")?.[1];
    if (part) {
      const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
      const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
      const claims = JSON.parse(window.atob(padded));
      sessionId = typeof claims?.session_id === "string" ? claims.session_id : "";
    }
  } catch {
    // Do not block installation UI when a token format differs.
  }

  const signInIdentity = sessionId || session.user.last_sign_in_at || "current";
  return `${userId}:${signInIdentity}`;
}

function getDismissedLoginKey() {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

export default function InstallPrompt({ suppressed = false }) {
  const { canInstall, installed, busy, install } = usePWAInstall();
  const [loginKey, setLoginKey] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [dismissedLoginKey, setDismissedLoginKey] = useState(getDismissedLoginKey);
  const [helpOpen, setHelpOpen] = useState(false);
  const [device, setDevice] = useState(() => (isIOS() ? "ios" : "android"));
  const [notice, setNotice] = useState("");

  useEffect(() => {
    // INITIAL_SESSION supplies the existing session on page load. SIGNED_IN
    // updates the key after login; refocus events for the same login keep it.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setLoginKey(getLoginKey(session));
        setAuthReady(true);
      }
    );
    return () => subscription.unsubscribe();
  }, []);

  const dismiss = () => {
    setHelpOpen(false);
    if (!loginKey) return;
    setDismissedLoginKey(loginKey);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, loginKey);
    } catch {
      // Still dismissed locally if storage is unavailable.
    }
  };

  useEffect(() => {
    if (!helpOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setHelpOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [helpOpen]);

  const handleInstallClick = async () => {
    if (busy) return;
    setNotice("");

    if (!canInstall) {
      setHelpOpen(true);
      return;
    }

    const result = await install();
    if (result === "accepted" || result === "installed") {
      dismiss();
      return;
    }
    if (result === "dismissed") {
      // The native prompt is one-use. Manual steps are available afterwards.
      setNotice("Installation was not completed. You can follow the browser steps below.");
    } else if (result !== "busy") {
      setNotice("The native installer is unavailable. Follow these steps instead.");
    }
    setHelpOpen(true);
  };

  // Never show inside an installed, standalone PWA. Wait for auth to settle
  // and never show on the public login/register pages or for logged-out users.
  const dismissedThisLogin = loginKey && dismissedLoginKey === loginKey;
  if (installed || suppressed || !authReady || !loginKey || dismissedThisLogin) {
    return null;
  }

  return (
    <>
      {/* Keep the banner above the app's fixed orange BottomBar. */}
      <div
        className="pointer-events-none fixed inset-x-0 z-[45] px-3"
        style={{ bottom: "calc(84px + env(safe-area-inset-bottom, 0px))" }}
      >
        <section
          className="pointer-events-auto mx-auto w-full max-w-[410px] rounded-[24px] border border-[#EFEFEF] bg-white p-4 shadow-[0_15px_48px_rgba(0,0,0,0.18)]"
          aria-label="Install Gwin"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FFF1E6] text-[#FF914D]">
              <Smartphone size={23} aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-[16px] font-black leading-tight text-[#333333]">Install Gwin</h2>
              <p className="mt-1 text-[12px] font-medium leading-relaxed text-[#757575]">
                Add Gwin to your Home Screen for quick access.
              </p>
            </div>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss installation reminder"
              className="-mr-1 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[#757575] hover:bg-[#F5F5F5]"
            >
              <X size={19} />
            </button>
          </div>
          <button
            type="button"
            onClick={handleInstallClick}
            disabled={busy}
            className="mt-4 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-2xl bg-[#FF914D] px-4 py-3 text-sm font-black text-white hover:bg-[#E9782F] disabled:opacity-50"
          >
            <Download size={17} aria-hidden="true" />
            {busy ? "Opening installer..." : "Install Gwin"}
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="mt-2 w-full rounded-xl py-2 text-xs font-semibold text-[#757575]"
          >
            Maybe later
          </button>
        </section>
      </div>

      {helpOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/55 px-4 py-5"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setHelpOpen(false);
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="gwin-install-help-title"
            className="w-full max-w-[430px] overflow-y-auto rounded-[26px] bg-white p-5 shadow-2xl"
            style={{ maxHeight: "calc(100dvh - 40px)" }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-[#FF914D]">Gwin App</p>
                <h2 id="gwin-install-help-title" className="mt-1 text-[22px] font-black leading-tight text-[#333333]">
                  Add to Home Screen
                </h2>
                <p className="mt-1 text-xs font-medium text-[#757575]">Follow the steps for your device.</p>
              </div>
              <button
                type="button"
                onClick={() => setHelpOpen(false)}
                aria-label="Close installation instructions"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#F4F4F6] text-[#757575]"
              >
                <X size={18} />
              </button>
            </div>
            {notice && (
              <p role="status" className="mt-4 rounded-xl bg-[#FFF4EC] p-3 text-xs font-bold text-[#6B5A4C]">
                {notice}
              </p>
            )}
            <div className="my-5 grid grid-cols-2 gap-2" role="group" aria-label="Device instructions">
              <button
                type="button"
                onClick={() => setDevice("android")}
                aria-pressed={device === "android"}
                className={`rounded-xl border px-3 py-2.5 text-sm font-bold ${device === "android" ? "border-[#FF914D] bg-[#FFF4EC] text-[#B9561D]" : "border-[#E5E5E5] text-[#757575]"}`}
              >
                Android
              </button>
              <button
                type="button"
                onClick={() => setDevice("ios")}
                aria-pressed={device === "ios"}
                className={`rounded-xl border px-3 py-2.5 text-sm font-bold ${device === "ios" ? "border-[#FF914D] bg-[#FFF4EC] text-[#B9561D]" : "border-[#E5E5E5] text-[#757575]"}`}
              >
                iPhone / iPad
              </button>
            </div>
            <InstallInstructions device={device} />
            <button
              type="button"
              onClick={() => setHelpOpen(false)}
              className="mt-5 min-h-[45px] w-full rounded-2xl bg-[#FF914D] px-4 py-3 text-sm font-black text-white"
            >
              Got it
            </button>
          </section>
        </div>
      )}
    </>
  );
}
